import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNotification } from "../../../../hooks/useNotification";
import { useErrorModal } from "../../../../hooks/useErrorModal";
import {
  Container,
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Divider,
  Checkbox,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  InputAdornment,
  Breadcrumbs,
  Link,
  CircularProgress,
  Chip,
  Avatar,
} from "@mui/material";
import { adminMenuItems } from "../../../../config/layout";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { DataGrid } from '@mui/x-data-grid';
import { useGetRolePermissionsQuery, useGetRolesQuery, useGetAllPermissionsQuery, useCreateRoleMutation, useUpdateRoleMutation, useUpdateRolePermissionsMutation } from "../../../../services/roleApi";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PermissionGuard } from "../../../../components/PermissionGuard";

// Hierarchy level color palette
const levelColors = {
  1: { bg: '#F0FDF4', border: '#86EFAC', text: '#15803D', dot: '#22C55E' },
  2: { bg: '#EFF6FF', border: '#93C5FD', text: '#1D4ED8', dot: '#3B82F6' },
  3: { bg: '#FFF7ED', border: '#FCA5A5', text: '#C2410C', dot: '#F97316' },
  4: { bg: '#FAF5FF', border: '#C4B5FD', text: '#7C3AED', dot: '#A855F7' },
  5: { bg: '#FFF1F2', border: '#FCA5A5', text: '#BE123C', dot: '#F43F5E' },
};

const getLevelColor = (levelStr) => {
  const match = String(levelStr || '').match(/(\d+)/);
  const num = match ? parseInt(match[0]) : 99;
  return levelColors[num] || { bg: '#F3F4F6', border: '#D1D5DB', text: '#374151', dot: '#6B7280' };
};

const formInputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'all 0.2s ease-in-out',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7522D1' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7522D1', borderWidth: '2px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#7522D1' },
};

const RoleAndPermission = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openAddRole, setOpenAddRole] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newRole, setNewRole] = useState({
    id: "",
    hierarchyLevel: "",
    roleName: "",
    description: "",
  });

  const currentBreadcrumbs = adminMenuItems.find((item) => item.id === 'role-and-permission')?.breadcrumbs || [];
  const { user } = useSelector((state) => state.auth);
  const { canCreate } = usePermissions();

  const { data: rolesData, isLoading: rolesLoading } = useGetRolesQuery();

  const isUuid = (value) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return value && typeof value === 'string' && uuidRegex.test(value);
  };

  const { data: rolePermissionsData } = useGetRolePermissionsQuery(selectedRole, { skip: !isUuid(selectedRole) });
  const { data: allPermissionsData } = useGetAllPermissionsQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [updateRolePermissions] = useUpdateRolePermissionsMutation();
  const [localPermissions, setLocalPermissions] = useState([]);

  const getHierarchyLevelValue = (level) => {
    if (!level) return 99;
    if (typeof level === 'number') return level;
    const levelMap = { 'Level 1': 1, 'Level 2': 2, 'Level 3': 3, 'Level 4': 4, 'Level 5': 5 };
    if (levelMap[level]) return levelMap[level];
    const match = String(level).match(/(\d+)/);
    return match ? parseInt(match[0], 10) : 99;
  };

  const filteredRoles = React.useMemo(() => {
    if (!Array.isArray(rolesData)) return [];
    let filtered = rolesData;
    if (user?.role?.hierarchyLevel) {
      const userLevelNum = getHierarchyLevelValue(user.role.hierarchyLevel);
      filtered = filtered.filter(role => getHierarchyLevelValue(role.hierarchyLevel || `Level ${role.level}`) >= userLevelNum);
    }
    if (user?.role?.code !== 'SUPER_ADMIN') {
      filtered = filtered.filter(role => role.code !== 'SUPER_ADMIN');
    }
    return filtered;
  }, [rolesData, user]);

  React.useEffect(() => {
    if (allPermissionsData) {
      if (rolePermissionsData) {
        const assignedMap = new Map();
        rolePermissionsData.forEach(p => assignedMap.set(p.permissionId, p));
        const merged = allPermissionsData.map(p => {
          const assigned = assignedMap.get(p.id);
          return {
            id: p.id, slNo: p.code || '', module: p.name || p.module,
            view: assigned?.canView || false, create: assigned?.canCreate || false,
            edit: assigned?.canEdit || false, delete: assigned?.canDelete || false,
            approve: assigned?.canApprove || false, specialPermissions: assigned?.specialPermission || '',
          };
        });
        setLocalPermissions(merged);
      } else {
        setLocalPermissions(allPermissionsData.map(p => ({
          id: p.id, slNo: p.code || '', module: p.module || p.name,
          view: false, create: false, edit: false, delete: false, approve: false, specialPermissions: '',
        })));
      }
    }
  }, [allPermissionsData, rolePermissionsData]);

  React.useEffect(() => {
    if (filteredRoles && filteredRoles.length > 0) {
      const currentRoleInFiltered = filteredRoles.find(r => r.id === selectedRole);
      if (!selectedRole || !currentRoleInFiltered) {
        const firstValidRole = filteredRoles.find(role => role.id && typeof role.id === 'string');
        if (firstValidRole) setSelectedRole(firstValidRole.id);
      }
    } else if (filteredRoles && filteredRoles.length === 0) {
      setSelectedRole(null);
    }
  }, [filteredRoles]);

  const groupedRoles = filteredRoles.reduce((acc, role) => {
    const levelKey = role.hierarchyLevel || `Level ${role.level}` || 'Other';
    if (!acc[levelKey]) acc[levelKey] = [];
    acc[levelKey].push(role);
    return acc;
  }, {});

  const handleRoleSelect = (roleId) => setSelectedRole(roleId);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleAddRoleClick = () => {
    setIsEditing(false);
    setNewRole({ id: "", hierarchyLevel: "", roleName: "", description: "" });
    setOpenAddRole(true);
  };

  const handleEditRoleClick = (e, role) => {
    e.stopPropagation();
    setIsEditing(true);
    setNewRole({ id: role.id, hierarchyLevel: role.hierarchyLevel || `Level ${role.level}`, roleName: role.name, description: role.description || "" });
    setOpenAddRole(true);
  };

  const handleAddRoleClose = () => {
    setOpenAddRole(false);
    setIsEditing(false);
    setNewRole({ id: "", hierarchyLevel: "", roleName: "", description: "" });
  };

  const hierarchyOptions = [
    { value: 'Level 1', label: 'Level 1 (Global Access)' },
    { value: 'Level 2', label: 'Level 2 (Entity Access)' },
    { value: 'Level 3', label: 'Level 3 (Location Access)' },
  ];

  const filteredHierarchyOptions = React.useMemo(() => {
    if (user?.role?.hierarchyLevel) {
      const userLevelNum = getHierarchyLevelValue(user.role.hierarchyLevel);
      return hierarchyOptions.filter(opt => getHierarchyLevelValue(opt.value) >= userLevelNum);
    }
    return hierarchyOptions;
  }, [user]);

  const [isLoading, setLoading] = useState(false);
  const { showNotification, Notification } = useNotification();
  const { showError, ErrorModal } = useErrorModal();

  const handleAddRoleSave = async () => {
    try {
      if (!newRole.hierarchyLevel || !newRole.roleName) {
        showNotification('Please fill in all required fields', 'error');
        return;
      }
      if (user?.role?.hierarchyLevel) {
        const userLevelNum = getHierarchyLevelValue(user.role.hierarchyLevel);
        const targetLevelNum = getHierarchyLevelValue(newRole.hierarchyLevel);
        if (targetLevelNum < userLevelNum) {
          showNotification('You cannot assign a hierarchy level higher than your own', 'error');
          return;
        }
      }
      setLoading(true);
      if (isEditing) {
        await updateRole({ id: newRole.id, name: newRole.roleName, description: newRole.description, level: getHierarchyLevelValue(newRole.hierarchyLevel), hierarchyLevel: newRole.hierarchyLevel }).unwrap();
        showNotification('Role updated successfully!', 'success');
      } else {
        await createRole({ name: newRole.roleName, description: newRole.description, level: getHierarchyLevelValue(newRole.hierarchyLevel), hierarchyLevel: newRole.hierarchyLevel }).unwrap();
        showNotification('Role created successfully!', 'success');
      }
      handleAddRoleClose();
    } catch (error) {
      console.error('Failed to save role:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (id, field) => {
    setLocalPermissions(localPermissions.map(p => p.id === id ? { ...p, [field]: !p[field] } : p));
  };

  const handleResetPermissions = () => {
    if (allPermissionsData) {
      if (rolePermissionsData) {
        const assignedMap = new Map();
        rolePermissionsData.forEach(p => assignedMap.set(p.permissionId, p));
        setLocalPermissions(allPermissionsData.map(p => {
          const assigned = assignedMap.get(p.id);
          return { id: p.id, slNo: p.code || '', module: p.name || p.module, view: assigned?.canView || false, create: assigned?.canCreate || false, edit: assigned?.canEdit || false, delete: assigned?.canDelete || false, approve: assigned?.canApprove || false, specialPermissions: assigned?.specialPermission || '' };
        }));
      } else {
        setLocalPermissions(allPermissionsData.map(p => ({ id: p.id, slNo: p.code || '', module: p.name || p.module, view: false, create: false, edit: false, delete: false, approve: false, specialPermissions: '' })));
      }
    }
  };

  const handleSavePermissions = async () => {
    try {
      setLoading(true);
      await updateRolePermissions({ roleId: selectedRole, permissions: localPermissions }).unwrap();
      showNotification('Permissions updated successfully!', 'success');
    } catch (error) {
      console.error("Failed to update permissions:", error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const selectedRoleObj = filteredRoles.find(r => r.id === selectedRole);

  return (
    <Container maxWidth="xl" sx={{ m: 0, height: "100%", p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{ marginBottom: 3, '& .MuiBreadcrumbs-li': { fontSize: '0.875rem' } }}
      >
        {currentBreadcrumbs.map((crumb, index) => (
          <Link
            key={crumb.path}
            underline={index === currentBreadcrumbs.length - 1 ? 'none' : 'hover'}
            color="inherit"
            sx={{
              cursor: index === currentBreadcrumbs.length - 1 ? 'default' : 'pointer',
              color: index === currentBreadcrumbs.length - 1 ? '#7522D1' : '#6B7280',
              fontWeight: index === currentBreadcrumbs.length - 1 ? 700 : 500,
              transition: 'all 0.2s',
              '&:hover': { color: '#7522D1' },
            }}
            onClick={() => index < currentBreadcrumbs.length - 1 && navigate(crumb.path)}
          >
            {crumb.label}
          </Link>
        ))}
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#030007", mb: 0.5, letterSpacing: '-0.02em' }}>
            Role & Permission
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", fontSize: "0.95rem", lineHeight: 1.6, fontWeight: 500 }}>
            Define roles and configure granular access permissions for each module.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Search */}
          <TextField
            placeholder="Search roles..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '10px', bgcolor: '#FFFFFF' }
            }}
            sx={{ minWidth: 240, ...formInputStyle }}
          />
          <PermissionGuard module="role" action="create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRoleClick}
              sx={{
                bgcolor: '#7522D1',
                color: '#fff',
                px: 3,
                py: 1.2,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(117,34,209,0.2)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { bgcolor: '#6A1DB0', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(117,34,209,0.3)' },
                whiteSpace: 'nowrap',
              }}
            >
              Add Role
            </Button>
          </PermissionGuard>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3} sx={{ height: '100%' }}>

        {/* Left Column — Roles List */}
        <Grid item size={{ xs: 12, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid rgba(117, 34, 209, 0.08)',
              boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
              overflow: 'hidden',
            }}
          >
            {/* Panel Header */}
            <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(117,34,209,0.06)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: '#F5EEFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldOutlinedIcon sx={{ fontSize: 18, color: '#7522D1' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', letterSpacing: '-0.01em' }}>
                Roles & Hierarchy
              </Typography>
            </Box>

            {/* Roles List */}
            <Box
              sx={{
                p: 2,
                maxHeight: '580px',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {rolesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ color: '#7522D1' }} />
                </Box>
              ) : Object.keys(groupedRoles).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body2" sx={{ color: '#9CA3AF', fontWeight: 600 }}>No roles found</Typography>
                </Box>
              ) : (
                Object.entries(groupedRoles).map(([level, roles]) => {
                  const colors = getLevelColor(level);
                  return (
                    <Box key={level} sx={{ mb: 2.5 }}>
                      {/* Level badge */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.dot, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ fontWeight: 800, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.72rem' }}>
                          {level}
                        </Typography>
                      </Box>

                      {/* Role items */}
                      {roles
                        .filter(role => role.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((role) => {
                          const isSelected = selectedRole === role.id;
                          return (
                            <Box
                              key={role.id}
                              onClick={() => handleRoleSelect(role.id)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                mb: 1,
                                borderRadius: '12px',
                                cursor: 'pointer',
                                border: isSelected ? '1.5px solid #7522D1' : '1.5px solid transparent',
                                backgroundColor: isSelected ? '#F5EEFC' : '#FFFFFF',
                                boxShadow: isSelected ? '0 4px 14px rgba(117,34,209,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
                                transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                                '&:hover': {
                                  backgroundColor: isSelected ? '#F5EEFC' : '#FAFAFA',
                                  borderColor: isSelected ? '#7522D1' : 'rgba(117,34,209,0.2)',
                                  transform: 'translateX(2px)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                <Avatar
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: '10px',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                    bgcolor: isSelected ? '#7522D1' : colors.bg,
                                    color: isSelected ? '#FFFFFF' : colors.text,
                                    border: `1px solid ${isSelected ? '#7522D1' : colors.border}`,
                                    flexShrink: 0,
                                  }}
                                >
                                  {role.name.slice(0, 2).toUpperCase()}
                                </Avatar>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isSelected ? 800 : 600,
                                    color: isSelected ? '#7522D1' : '#111827',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {role.name}
                                </Typography>
                              </Box>
                              <PermissionGuard module="role" action="edit">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleEditRoleClick(e, role)}
                                  sx={{
                                    color: isSelected ? '#7522D1' : '#9CA3AF',
                                    '&:hover': { color: '#7522D1', bgcolor: 'rgba(117,34,209,0.08)' },
                                    borderRadius: '8px',
                                    p: 0.5,
                                    flexShrink: 0,
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </PermissionGuard>
                            </Box>
                          );
                        })}
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column — Permissions Grid */}
        <Grid item size={{ xs: 12, md: 9 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid rgba(117, 34, 209, 0.08)',
              boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Panel Header */}
            <Box
              sx={{
                px: 3,
                py: 2.5,
                borderBottom: '1px solid rgba(117,34,209,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', letterSpacing: '-0.01em' }}>
                  Permissions
                </Typography>
                {selectedRoleObj && (
                  <Chip
                    label={selectedRoleObj.name}
                    size="small"
                    sx={{
                      bgcolor: '#F5EEFC',
                      color: '#7522D1',
                      fontWeight: 700,
                      border: '1px solid rgba(117,34,209,0.15)',
                      borderRadius: '8px',
                      fontSize: '0.78rem',
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* DataGrid */}
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <DataGrid
                rows={localPermissions}
                columns={[
                  {
                    field: "slNo",
                    headerName: "CODE",
                    width: 120,
                    sortable: true,
                    renderCell: (params) => (
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B7280', letterSpacing: '0.04em' }}>
                        {params.value}
                      </Typography>
                    ),
                  },
                  {
                    field: "module",
                    headerName: "MODULE",
                    width: 200,
                    sortable: true,
                    flex: 1,
                    renderCell: (params) => (
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                        {params.value}
                      </Typography>
                    ),
                  },
                  ...[
                    { field: "view", headerName: "VIEW" },
                    { field: "create", headerName: "CREATE" },
                    { field: "edit", headerName: "EDIT" },
                    { field: "delete", headerName: "DELETE" },
                    { field: "approve", headerName: "APPROVE" },
                  ].map(col => ({
                    ...col,
                    width: 90,
                    sortable: false,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <Checkbox
                        checked={params.value}
                        onChange={() => handlePermissionChange(params.row.id, col.field)}
                        sx={{
                          color: '#D1D5DB',
                          '&.Mui-checked': { color: '#7522D1' },
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                        }}
                      />
                    ),
                  })),
                  {
                    field: "specialPermissions",
                    headerName: "SPECIAL",
                    width: 160,
                    sortable: false,
                    renderCell: (params) => params.value ? (
                      <Chip label={params.value} size="small" sx={{ bgcolor: '#F5EEFC', color: '#7522D1', fontWeight: 700, fontSize: '0.72rem', borderRadius: '6px' }} />
                    ) : null,
                  },
                ]}
                disableRowSelectionOnClick
                disableColumnMenu
                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                pageSizeOptions={[5, 10, 25, 50]}
                rowHeight={56}
                columnHeaderHeight={52}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#F8F5FC',
                    color: '#42374F',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    letterSpacing: '0.06em',
                    borderBottom: '1px solid rgba(117,34,209,0.08)',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(117,34,209,0.04)',
                    color: '#374151',
                    fontSize: '0.875rem',
                    alignItems: 'center',
                    display: 'flex',
                  },
                  '& .MuiDataGrid-row': {
                    transition: 'background-color 0.15s ease',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(117,34,209,0.02)',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid rgba(117,34,209,0.06)',
                    backgroundColor: '#FAFAFA',
                  },
                  '& .MuiButton-textPrimary': {
                    color: '#7522D1',
                    fontWeight: 700,
                  },
                  '& .MuiTablePagination-root': {
                    color: '#6B7280',
                    fontWeight: 600,
                  },
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                px: 3,
                py: 2.5,
                borderTop: "1px solid rgba(117,34,209,0.06)",
                bgcolor: '#FAFAFA',
              }}
            >
              <PermissionGuard module="permission" action="edit">
                <Button
                  variant="outlined"
                  onClick={handleResetPermissions}
                  sx={{
                    textTransform: 'none',
                    px: 4,
                    py: 1.2,
                    borderRadius: '12px',
                    border: '1px solid rgba(117,34,209,0.2)',
                    color: '#7522D1',
                    fontWeight: 700,
                    '&:hover': { borderColor: '#7522D1', bgcolor: '#F5EEFC', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s',
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSavePermissions}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{
                    textTransform: 'none',
                    px: 5,
                    py: 1.2,
                    borderRadius: '12px',
                    bgcolor: '#7522D1',
                    fontWeight: 750,
                    boxShadow: '0 4px 14px rgba(117,34,209,0.2)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { bgcolor: '#6A1DB0', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(117,34,209,0.3)' },
                  }}
                >
                  {isLoading ? 'Saving...' : 'Save Permissions'}
                </Button>
              </PermissionGuard>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add / Edit Role Dialog */}
      <Dialog
        open={openAddRole}
        onClose={handleAddRoleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(117,34,209,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ px: 4, pt: 3.5, pb: 2, borderBottom: '1px solid rgba(117,34,209,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#F5EEFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldOutlinedIcon sx={{ fontSize: 20, color: '#7522D1' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#030007', letterSpacing: '-0.01em' }}>
                {isEditing ? 'Edit Role' : 'Create New Role'}
              </Typography>
            </Box>
            <IconButton onClick={handleAddRoleClose} size="small" sx={{ bgcolor: '#F3F4F6', borderRadius: '8px', '&:hover': { bgcolor: '#E5E7EB' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3.5 }}>
          <Stack spacing={3}>
            <FormControl fullWidth sx={formInputStyle}>
              <InputLabel>Hierarchy Level *</InputLabel>
              <Select
                value={newRole.hierarchyLevel}
                label="Hierarchy Level *"
                onChange={(e) => setNewRole({ ...newRole, hierarchyLevel: e.target.value })}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="">Select a level</MenuItem>
                {filteredHierarchyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Role Name *"
              fullWidth
              placeholder="e.g., HR Manager, Finance Lead"
              value={newRole.roleName}
              onChange={(e) => setNewRole({ ...newRole, roleName: e.target.value })}
              sx={formInputStyle}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              placeholder="Describe the responsibilities and scope of this role..."
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              sx={formInputStyle}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid rgba(117,34,209,0.06)', gap: 1.5 }}>
          <Button
            onClick={handleAddRoleClose}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.2,
              borderRadius: '12px',
              border: '1px solid rgba(117,34,209,0.2)',
              color: '#7522D1',
              fontWeight: 700,
              '&:hover': { borderColor: '#7522D1', bgcolor: '#F5EEFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRoleSave}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              px: 5,
              py: 1.2,
              borderRadius: '12px',
              bgcolor: '#7522D1',
              fontWeight: 750,
              boxShadow: '0 4px 14px rgba(117,34,209,0.2)',
              '&:hover': { bgcolor: '#6A1DB0', boxShadow: '0 6px 20px rgba(117,34,209,0.3)' },
            }}
          >
            {isLoading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Role')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
      <ErrorModal />
    </Container>
  );
};

export default RoleAndPermission;
